import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { LABEL_TEMPLATES } from '../../utils/printUtils';

/**
 * Shipping Label Component (Sticker Template)
 * สำหรับพิมพ์ก่อนมี tracking number
 */
const ShippingLabel = ({ 
  order, 
  store, 
  templateType = 'sticker',
  editedData = null 
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  
  // ใช้ข้อมูลที่แก้ไขแล้ว หรือข้อมูลเดิม
  const displayData = editedData || {
    buyerName: order?.buyerName || '',
    buyerPhone: order?.buyerPhone || '',
    shippingAddress: order?.shippingAddress || '',
    notes: order?.notes || ''
  };

  // Generate QR Code สำหรับเบอร์โทรผู้รับ
  useEffect(() => {
    const generateQRCode = async () => {
      if (!displayData.buyerPhone) return;
      
      try {
        // สร้าง QR Code ที่มีข้อมูล: tel:เบอร์โทร
        const qrData = `tel:${displayData.buyerPhone}`;
        const qrCodeDataURL = await QRCode.toDataURL(qrData, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrCodeUrl(qrCodeDataURL);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCode();
  }, [displayData.buyerPhone]);

  const template = LABEL_TEMPLATES[templateType.toUpperCase()] || LABEL_TEMPLATES.STICKER;

  return (
    <div 
      id="shipping-label-print"
      className="print-content"
      style={{
        width: template.width,
        minHeight: template.height,
        margin: '0 auto',
        padding: '0.8cm',
        fontFamily: "'Sarabun', 'Kanit', Arial, sans-serif",
        backgroundColor: '#ffffff',
        border: '1px dashed #ccc',
        boxSizing: 'border-box',
        color: '#000000',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
        colorAdjust: 'exact'
      }}
    >
      {/* Header - Store Info */}
      <div 
        className="mb-3 pb-3 border-b-2 border-gray-800" 
        style={{ 
          borderBottomWidth: '2px',
          borderBottomColor: '#1f2937',
          borderBottomStyle: 'solid',
          marginBottom: '12px',
          paddingBottom: '12px'
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1">
            {store?.logo && (
              <div className="mb-2">
                <img 
                  src={store.logo} 
                  alt={store.name}
                  style={{ 
                    maxHeight: '35px', 
                    maxWidth: '100px', 
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              </div>
            )}
            <h2 
              className="text-base font-bold text-gray-800 mb-1" 
              style={{ 
                fontSize: '16px', 
                lineHeight: '1.4',
                fontWeight: '700',
                color: '#1f2937',
                marginBottom: '4px'
              }}
            >
              {store?.name || 'ร้านค้า'}
            </h2>
            {store?.contact?.phone && (
              <p 
                className="text-xs text-gray-600" 
                style={{ 
                  fontSize: '11px',
                  color: '#4b5563'
                }}
              >
                📞 {store.contact.phone}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Order Info */}
      <div 
        className="mb-3 pb-2 border-b border-gray-200" 
        style={{ 
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottomColor: '#e5e7eb',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid'
        }}
      >
        <div className="flex justify-between items-center mb-1" style={{ marginBottom: '4px' }}>
          <span 
            className="text-xs text-gray-600" 
            style={{ 
              fontSize: '10px',
              color: '#4b5563'
            }}
          >
            ออเดอร์:
          </span>
          <span 
            className="text-xs font-bold text-gray-800" 
            style={{ 
              fontSize: '12px',
              fontWeight: '700',
              color: '#1f2937'
            }}
          >
            #{order?.orderNumber || ''}
          </span>
        </div>
        <div 
          className="text-xs text-gray-600" 
          style={{ 
            fontSize: '10px',
            color: '#4b5563'
          }}
        >
          วันที่: {order?.createdAt ? new Date(order.createdAt.toDate()).toLocaleDateString('th-TH') : ''}
        </div>
      </div>

      {/* Recipient Info */}
      <div className="mb-3 p-2.5 bg-gray-50 rounded border border-gray-300" style={{ padding: '8px' }}>
        <div className="text-xs font-bold text-gray-800 mb-2" style={{ fontSize: '11px', marginBottom: '6px' }}>
          📦 ผู้รับสินค้า
        </div>
        <div className="text-sm font-bold text-gray-900 mb-1.5" style={{ fontSize: '13px', marginBottom: '4px', lineHeight: '1.4' }}>
          {displayData.buyerName || '-'}
        </div>
        <div className="text-xs text-gray-700 mb-2" style={{ fontSize: '11px', marginBottom: '6px' }}>
          📞 {displayData.buyerPhone || '-'}
        </div>
        <div className="text-xs text-gray-700 leading-relaxed" style={{ fontSize: '10px', lineHeight: '1.5' }}>
          {displayData.shippingAddress?.split('\n').map((line, i) => (
            <div key={i} style={{ marginBottom: '2px' }}>{line}</div>
          )) || '-'}
        </div>
      </div>

      {/* QR Code */}
      {qrCodeUrl && (
        <div className="mb-3 text-center">
          <div 
            className="text-xs font-semibold text-gray-700 mb-1.5" 
            style={{ 
              fontSize: '10px', 
              marginBottom: '4px',
              fontWeight: '600',
              color: '#374151'
            }}
          >
            📱 Scan เพื่อโทรหา
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '4px' }}>
            <img 
              src={qrCodeUrl} 
              alt="QR Code"
              style={{ 
                width: '100px', 
                height: '100px',
                border: '2px solid #000000',
                borderStyle: 'solid',
                display: 'block'
              }}
            />
          </div>
          <div 
            className="text-xs text-gray-600" 
            style={{ 
              fontSize: '10px',
              color: '#4b5563'
            }}
          >
            {displayData.buyerPhone}
          </div>
        </div>
      )}

      {/* Items Summary */}
      {order?.items && order.items.length > 0 && (
        <div 
          className="mb-3 p-2 bg-gray-50 rounded border border-gray-300" 
          style={{ 
            padding: '6px',
            backgroundColor: '#f9fafb',
            borderColor: '#d1d5db',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '0.5rem'
          }}
        >
          <div 
            className="text-xs font-semibold text-gray-700 mb-1" 
            style={{ 
              fontSize: '10px', 
              marginBottom: '4px',
              fontWeight: '600',
              color: '#374151'
            }}
          >
            📋 รายการสินค้า:
          </div>
          {order.items.map((item, index) => (
            <div 
              key={index} 
              className="text-xs text-gray-700" 
              style={{ 
                fontSize: '10px', 
                lineHeight: '1.4', 
                marginBottom: '2px',
                color: '#374151'
              }}
            >
              • {item.productName} ({item.quantity} {item.unit || 'ชิ้น'})
            </div>
          ))}
        </div>
      )}

      {/* Notes */}
      {displayData.notes && (
        <div 
          className="mb-3 p-2 bg-yellow-50 rounded border border-yellow-300" 
          style={{ 
            padding: '6px',
            backgroundColor: '#fefce8',
            borderColor: '#fde047',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '0.5rem'
          }}
        >
          <div 
            className="text-xs font-semibold text-gray-700 mb-1" 
            style={{ 
              fontSize: '10px', 
              marginBottom: '4px',
              fontWeight: '600',
              color: '#374151'
            }}
          >
            📝 หมายเหตุ:
          </div>
          <div 
            className="text-xs text-gray-700" 
            style={{ 
              fontSize: '10px', 
              lineHeight: '1.4',
              color: '#374151'
            }}
          >
            {displayData.notes}
          </div>
        </div>
      )}

      {/* Footer */}
      <div 
        className="mt-3 pt-2 border-t border-gray-300 text-center" 
        style={{ 
          marginTop: '8px', 
          paddingTop: '6px',
          borderTopColor: '#d1d5db',
          borderTopWidth: '1px',
          borderTopStyle: 'solid'
        }}
      >
        <div 
          className="text-xs text-gray-500" 
          style={{ 
            fontSize: '9px',
            color: '#6b7280'
          }}
        >
          พิมพ์เมื่อ: {new Date().toLocaleString('th-TH')}
        </div>
      </div>
    </div>
  );
};

export default ShippingLabel;

